/**
 * InlinePhotoUpload — compact photo/document upload widget that lives
 * directly inside the Customer / Guarantor form.
 *
 * Modes:
 *  - "live"   (default): customerId is present → uploads immediately to the server.
 *               Endpoint = props.endpoint  (e.g. /customers/{id}/documents/photo)
 *  - "staged": no customerId yet → the parent provides onStage(file) and stagedFile prop
 *               so the file is held in memory until the customer is saved.
 *
 * Always emits a stable thumbnail preview from either:
 *   - currentUrl (server-side URL — prefixed by REACT_APP_BACKEND_URL)
 *   - stagedFile (local File object — turned into object URL)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Upload, X, Eye, Image as ImageIcon, Camera as CameraIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { isNativePlatform } from "../lib/platform";
import { pickImage, Source } from "../lib/nativeCamera";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function InlinePhotoUpload({
  label,
  endpoint,        // live mode only
  currentUrl,      // existing server URL (live mode)
  stagedFile,      // File object (staged mode)
  onStage,         // (file|null) => void (staged mode)
  onUploaded,      // callback after a live upload (refreshes parent state)
  size = "md",
  testId,
  variant = "square",
}) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  // Derive the local-preview URL from the staged File via useMemo so we don't
  // need to call setState inside useEffect.
  const localPreview = useMemo(
    () => (stagedFile ? URL.createObjectURL(stagedFile) : null),
    [stagedFile],
  );
  useEffect(() => {
    // Revoke the URL when it changes or the component unmounts.
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const previewSrc = localPreview || (currentUrl ? `${API_BASE}${currentUrl}` : null);

  const onFile = async (file) => {
    if (!file) return;
    if (onStage) {
      onStage(file);
      return;
    }
    if (!endpoint) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} upload failed`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Trigger pick — on Android use the Capacitor Camera (prompt dialog
   * gives Camera / Gallery choice). On web → click the hidden <input>.
   */
  const triggerPick = async (source = Source.PROMPT) => {
    if (isNativePlatform()) {
      try {
        setBusy(true);
        const f = await pickImage({ source, filename: `${label || "photo"}.jpg` });
        if (f) await onFile(f);
      } catch (err) {
        const msg = err?.message || "";
        if (!/cancel/i.test(msg)) toast.error(msg || "Camera nahi khul saka");
      } finally {
        setBusy(false);
      }
      return;
    }
    inputRef.current?.click();
  };

  const clear = () => {
    if (onStage) onStage(null);
  };

  const sizeClass =
    variant === "wide"
      ? "h-24 w-full"
      : size === "lg"
      ? "h-32 w-32"
      : size === "sm"
      ? "h-16 w-16"
      : "h-24 w-24";

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-600">{label}</div>
      <div className={`relative ${sizeClass} group`}>
        {previewSrc ? (
          <>
            <img
              src={previewSrc}
              alt={label}
              className="w-full h-full object-cover rounded-md border border-slate-300"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded-md">
              <a
                href={previewSrc}
                target="_blank"
                rel="noreferrer"
                title="View"
                className="bg-white/90 hover:bg-white text-slate-700 p-1.5 rounded-md"
              >
                <Eye className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                title="Replace"
                className="bg-white/90 hover:bg-white text-slate-700 p-1.5 rounded-md"
                data-testid={`${testId}-replace`}
              >
                <Upload className="w-3.5 h-3.5" />
              </button>
              {onStage && (
                <button
                  type="button"
                  onClick={clear}
                  title="Remove"
                  className="bg-white/90 hover:bg-white text-red-600 p-1.5 rounded-md"
                  data-testid={`${testId}-clear`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => triggerPick()}
            disabled={busy}
            className={`w-full h-full rounded-md border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-emerald-500 flex flex-col items-center justify-center gap-1 text-slate-500 disabled:opacity-50 transition-colors`}
            data-testid={`${testId}-upload`}
          >
            {isNativePlatform() ? <CameraIcon className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              {busy ? "Uploading…" : isNativePlatform() ? "Camera / Gallery" : "Upload"}
            </span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            // Web fallback path → compress before upload.
            try {
              const { default: imageCompression } = await import("browser-image-compression");
              const compressed = (f.size > 800 * 1024 && f.type?.startsWith("image/"))
                ? await imageCompression(f, { maxSizeMB: 0.8, maxWidthOrHeight: 1600, useWebWorker: true, initialQuality: 0.8 })
                : f;
              const out = compressed instanceof File ? compressed : new File([compressed], f.name, { type: compressed.type || f.type });
              onFile(out);
            } catch {
              onFile(f);
            }
          }}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}
